import { useEffect, useState, type FormEvent } from "react";
import { useAuth } from "../contexts/AuthContext";
import { ApiError } from "../api/client";
import { createInvite, listInvites, revokeInvite, type InviteRow } from "../api/auth";

export function InvitesPage() {
  const { api } = useAuth();
  const [rows, setRows] = useState<InviteRow[]>([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [expiresIn, setExpiresIn] = useState("7");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newInvite, setNewInvite] = useState<{ code: string; email: string | null; expires_at: string } | null>(null);

  async function refresh() {
    try { setRows((await listInvites(api)).invites); }
    catch (e) { setError(e instanceof ApiError ? e.message : "load failed"); }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null); setNewInvite(null);
    try {
      const r = await createInvite(api, {
        email: email || undefined,
        role,
        expires_in_days: parseInt(expiresIn, 10) || 7,
      });
      setNewInvite({ code: r.invite.code, email: r.invite.email, expires_at: r.invite.expires_at });
      setEmail("");
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "create failed");
    } finally {
      setBusy(false);
    }
  }

  async function doRevoke(id: string) {
    if (!confirm("Revoke this invite?")) return;
    try { await revokeInvite(api, id); await refresh(); }
    catch (e) { setError(e instanceof ApiError ? e.message : "revoke failed"); }
  }

  return (
    <div className="vstack">
      <div>
        <h1>Invites</h1>
        <p className="meta">Mint invite codes to bring other family members onto your account.</p>
      </div>

      <form className="card" onSubmit={submit}>
        <h2>New invite</h2>
        <div className="field">
          <label htmlFor="email">Pin to email (optional)</label>
          <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="role">Role</label>
          <select id="role" value={role} onChange={(e) => setRole(e.target.value)}
            style={{ background: "var(--surface)", border: "1px solid var(--border)", color: "var(--text)", padding: "10px 12px", borderRadius: "4px" }}>
            <option value="member">member</option>
            <option value="owner">owner</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="expires">Expires in (days, 1–90)</label>
          <input id="expires" type="number" min="1" max="90" value={expiresIn}
            onChange={(e) => setExpiresIn(e.target.value)} />
        </div>
        {error && <div className="error">{error}</div>}
        <button className="btn primary" type="submit" disabled={busy}>
          {busy ? "Creating…" : "Create invite"}
        </button>
        {newInvite && (
          <div style={{ marginTop: 16 }}>
            <p className="meta">Share this code with the invitee. It's shown <strong>once</strong>.</p>
            <div className="code">{newInvite.code}</div>
            <p className="subtle">Expires {newInvite.expires_at}{newInvite.email ? ` · pinned to ${newInvite.email}` : ""}</p>
          </div>
        )}
      </form>

      <div className="card">
        <h2>Active + historical</h2>
        {rows.length === 0 && <p className="meta">No invites yet.</p>}
        {rows.map((r) => (
          <div className="row" key={r.id}>
            <div>
              <div className="row-title">{r.email ?? "(any email)"} · {r.role}</div>
              <div className="row-meta">
                {r.status} · expires {r.expires_at}
                {r.redeemed_by_account_id ? ` · redeemed by ${r.redeemed_by_account_id}` : ""}
              </div>
            </div>
            {r.status === "pending" && (
              <button className="btn danger" onClick={() => doRevoke(r.id)}>Revoke</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
